import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { Breadcrumbs, Button, SkeletonBase } from "@unified-checkout/design-system";
import { Check, Xmark } from "@unified-checkout/design-system/icons/index";
import { FOUR_SECONDS } from "@app/dashboard/constants/shared";
import type { IButton } from "libs/design-system/src/types/Button/button";
import type { IXeroHeader } from "@app/dashboard/types/innerLayout/xero/header";
import { disconnectXeroOrganization } from "@app/dashboard/services/inner_layout/xero/disconnect";
import { DisconnectModal } from "../Modals/disconnectModal";
import { useSettingsUtils } from "../../Settings/useSettingsUtils";
import { notify } from "@app/dashboard/utils/notifier";
import {
  MAX_NOTIFICATION_WIDTH,
  NOTIFICATION_TIMEOUT,
} from "@app/dashboard/constants/transactions";
import styles from "./header.module.scss";

export const Header = ({
  isLoading,
  isConnect,
  apiKeys,
  refetchData,
  onDisconnectSuccess
}: IXeroHeader
) => {
  const { t } = useTranslation();
  const { internalPagesBreadcrumbs } = useSettingsUtils();

  const [modalOpen, setModalOpen] = useState(false);

  const {
    mutate: disconnectOrganizationMutate,
    isPending: disconnectOrganizationLoading,
  } = useMutation({
    mutationKey: [QUERY_KEYS.DISCONNECT_XERO_ORGANIZATION],
    mutationFn: () => disconnectXeroOrganization(apiKeys as string),
    onSuccess: () => {
      setModalOpen(false);
      handleNotificationState("success", t('innerLayout.xeroPage.modals.succesDisconnect'));
      onDisconnectSuccess?.();
      refetchData?.();
    },
  })

  const handleNotificationState = (
    type: "success" | "error" | null,
    message: string,
    refetchFn?: () => void
  ) => {
    notify[type as "success" | "error"]?.(message, {
      maxWidth: MAX_NOTIFICATION_WIDTH,
      timeout: NOTIFICATION_TIMEOUT,
      icon: type === "error" ? Xmark : Check,
    })
  };

  const headerActionBtnsProps = {
    size: "md",
    color: "primary",
    variant: "contained",
    text: t('innerLayout.xeroPage.header.disconnectOrganization'),
    onClickHandler: () => setModalOpen(true)
  } as IButton;

  const disConnectProps = {
    isModalOpen: modalOpen,
    setIsModalOpen: setModalOpen,
    disconnectOrganizationMutate: disconnectOrganizationMutate,
    disconnectOrganizationLoading: disconnectOrganizationLoading,
  }

  return (
    <div className={styles["header"]}>
      <div className={styles["header__breadcrumbs"]}>
        {
          isLoading ?
            <SkeletonBase height="1.5rem" />
            :
            <Breadcrumbs linksProps={internalPagesBreadcrumbs.XERO} />
        }
      </div>
      {
        isLoading ?
          <SkeletonBase height="3.75rem" />
          :
          <div className={styles["header__main-info-box"]}>

            <div className={styles["header__main-info-box__info"]}>

              <div className={styles["header__main-info-box__info__xero-box"]}>
                <span>
                  {t('innerLayout.xeroPage.header.xeroIntegration')}
                </span>
              </div>
            </div>

            <div className={styles["header__main-info-box__actions"]}>
              {isConnect ? <Button {...headerActionBtnsProps} /> : null}
            </div>

          </div>
      }   

      <div className={styles["header__modal"]}>
        <DisconnectModal {...disConnectProps} />
      </div>

    </div>
  )
};